import * as React from 'react';

import type { ContentBranchHistoryItem, Revision } from '@gitbook/hive-core';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { useHiveClient } from './useHiveClient';

interface HiveBranchHistoryInput {
    /** The ID of the content. */
    contentId: string;
    /** The ID of the branch. */
    branchId: string;
    /** The limit of the history. */
    limit: number;
    /** The initial page of the history. */
    initialPage: Revision['id'];
    /** Whether to only include git revisions. */
    git?: boolean;
    /** ID of the revision to stop at */
    stopAt?: Revision['id'];
}

/**
 * Get the paginated history of a branch.
 */
export function useHiveBranchHistory(input: HiveBranchHistoryInput): {
    history: ContentBranchHistoryItem[];
    loadMore?: () => Promise<void>;
} {
    const { contentId, branchId, limit, initialPage, git = false, stopAt } = input;

    const client = useHiveClient();

    const [history, setHistory] = React.useState<ContentBranchHistoryItem[]>([]);
    const [nextPage, setNextPage] = React.useState<string | undefined>(initialPage);

    const initialHistory = getSuspense(
        client.store.getBranchHistory(contentId, branchId, {
            page: initialPage,
            limit,
            git,
        })
    );

    const fetchData = React.useCallback(
        async (page: string, initial: boolean = false) => {
            const data = await client.store.getBranchHistory(contentId, branchId, {
                page,
                limit,
                git,
            });
            if (data) {
                setHistory((prev: ContentBranchHistoryItem[]) => {
                    if (initial) {
                        const index = data.history.findIndex(
                            (item) => item.revision.id === prev[0]?.revision.id
                        );

                        if (index === -1) {
                            return data.history;
                        }

                        return dedupeHistory([...data.history.slice(0, index), ...prev]);
                    }

                    return dedupeHistory([...prev, ...data.history]);
                });
                setNextPage(data.next?.page);
            }
        },
        [contentId, branchId, limit, git, client]
    );

    // Fetch the first page of history
    React.useEffect(() => {
        fetchData(initialPage, true);
    }, [fetchData, initialPage]);

    const loadMore = React.useCallback(async () => {
        if (nextPage) {
            await fetchData(nextPage);
        }
    }, [nextPage, fetchData]);

    const fullHistory = history.length ? history : initialHistory.history;

    return React.useMemo(() => {
        let stopAtIndex = -1;
        if (stopAt) {
            stopAtIndex = fullHistory.findIndex((item) => item.revision.parents.includes(stopAt));
        }

        return {
            history: stopAtIndex < 0 ? fullHistory : fullHistory.slice(0, stopAtIndex),
            loadMore: nextPage && stopAtIndex < 0 ? loadMore : undefined,
        };
    }, [fullHistory, nextPage, loadMore, stopAt]);
}

function dedupeHistory(history: ContentBranchHistoryItem[]): ContentBranchHistoryItem[] {
    const seen = new Set<string>();
    return history.filter((item) => {
        const id = item.revision.id;
        if (seen.has(id)) {
            return false;
        }
        seen.add(id);
        return true;
    });
}
